export enum AuthEnv {
  Test = 'https://auth-orca.test.k8s.onepeloton',
  Stage = 'https://auth-orca.stage.k8s.onepeloton',
  Prod = 'https://auth-orca.onepeloton',
}

export type AuthUser = {
  userId: string;
  email: string;
  username?: string;
  avatarUrl?: string;
};

export type AuthStatus = 'init' | 'loading' | 'authenticated' | 'unauthenticated';

export type LoginRedirectOpts = {
  fragment?: string;
  authorizationParams?: Record<string, string | number | boolean | undefined>;
  redirectMethod?: 'replace' | 'assign';
};

// ---------------------------------------------------------------------------
// Monitoring event types — emitted by @auth modules through optional
// callbacks so that consumer apps can wire them to their own observability
// stack (Datadog RUM, Sentry, console). The package itself stays
// monitoring-agnostic; see `@auth/monitoring` for the standard adapter.
// ---------------------------------------------------------------------------

/** Funnel + lifecycle events emitted by AuthProvider. */
export type AuthProviderEvent =
  | 'init.start'
  | 'init.complete'
  | 'silent.attempted'
  | 'silent.success'
  | 'silent.failed'
  | 'silent.skipped_no_marker_cookie'
  | 'token.awaited_init'
  | 'token.skipped_uninitialized'
  | 'token.skipped_unauthenticated'
  | 'token.skipped_backoff'
  | 'token.network_attempted'
  | 'token.refresh_attempted'
  | 'callback.received'
  | 'callback.skipped_bypass_path'
  | 'callback.success'
  | 'callback.error'
  | 'login.redirect'
  | 'logout.fired'
  | 'logout.error';

/** Action discriminator already passed to AuthProviderProps.onError. */
export type AuthProviderErrorAction =
  | 'handleRedirectCallback'
  | 'getTokenSilently'
  | 'getIdTokenClaims'
  | 'isAuthenticated'
  | 'logout';

/** Funnel events emitted by CallbackHandler. */
export type CallbackHandlerEvent =
  | 'received'
  | 'success'
  | 'legacy_session.posted'
  | 'legacy_session.failed'
  | 'social_error'
  | 'not_authenticated';

/**
 * Funnel events emitted by the `useLogout` hook.
 *
 * Intentionally distinct from `AuthProviderEvent`'s `'logout.fired'` /
 * `'logout.error'` so that both hooks can safely share an
 * `auth.logout.${event}` action-name namespace in observability sinks
 * (e.g. Datadog RUM) without collisions / double-counting. `useLogout`
 * covers the **pre-Auth0** phase (hook invocation + legacy-session
 * cleanup); the AuthProvider events cover the Auth0 redirect itself.
 */
export type LogoutEvent =
  | 'started'
  | 'legacy_session_deleted'
  | 'legacy_session_delete_failed';

/** Per-call options for `AuthContextValue.logout`. */
export type LogoutOpts = {
  /**
   * Clear the local Auth0 session only: skip the `/v2/logout` round-trip so
   * the browser stays on the current page and the IdP session survives. The
   * caller owns whatever navigation happens next, and `returnTo` is ignored.
   *
   * Maps onto auth0-spa-js' `logout({ openUrl: false })`. Use it for flows
   * that need to drop cached tokens and then route somewhere specific
   * themselves — e.g. remote activation's auth-mismatch redirect, which
   * sends the member to the auth app's error page rather than signing them
   * out of every Peloton app.
   *
   * @default false
   */
  localOnly?: boolean;
};

/** Source of the bearer token attached to an outbound request. */
export type ResolvedTokenSource = 'auth0' | 'checkout' | 'none';

/**
 * Funnel events emitted by the response-side 401-recovery primitive
 * `useOauthErrorHandler` (Axios). Fires at four checkpoints so consumer
 * apps can wire DD monitors for refresh failures and logout-loop
 * precursors:
 *
 *  - `attempted`     — a 401 was observed and we are about to refresh.
 *  - `success`       — refresh + retry succeeded; user is recovered.
 *  - `skipped`       — 401 hit a skip-list URL or an already-retried
 *                      request; the original error is propagated.
 *  - `failed`        — refresh failed OR the retry itself returned 401.
 *                      The consumer's `onAuthFailure` handler fires
 *                      next (typically driving `useLogout`).
 *
 * Names are intentionally disjoint from `AuthProviderEvent` and
 * `LogoutEvent` so all three can share an `auth.*` action-name namespace
 * in observability sinks (e.g. Datadog RUM) without collisions.
 */
export type AuthErrorRecoveryEvent =
  | 'attempted'
  | 'success'
  | 'skipped'
  | 'failed'
  | 'forbidden'
  | 'not_found'
  | 'not_found_skipped'
  | 'opt_out';

/**
 * Decoded ID-token payload plus the raw signed JWT.
 *
 * `claims` holds the standard OIDC claims (`sub`, `email`, etc.) plus any
 * Peloton custom claims (e.g. `http://onepeloton.com/user_id`).
 *
 * `raw` is the original signed JWT string. Pass it to backends that
 * verify the ID token themselves (e.g. TeamUp).
 */
export type IdToken = {
  raw: string;
  claims: Record<string, unknown>;
};

export type AuthContextValue = {
  status: AuthStatus;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  getAccessToken: () => Promise<string | null>;
  refreshAccessToken: () => Promise<string | null>;
  getIdToken: (options?: { throwOnError?: boolean }) => Promise<IdToken | null>;
  loginWithRedirect: (returnTo?: string, opts?: LoginRedirectOpts) => Promise<void>;
  logout: (returnTo?: string, opts?: LogoutOpts) => Promise<void>;
  error: Error | null;
  returnTo: string | null;
};
